import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import './InvoiceSettingsModal.scss';

interface InvoiceSettings {
    price_tolerance_percent: number;
    quantity_strict_match: boolean;
    auto_approve_limit: number;
}

interface InvoiceSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const InvoiceSettingsModal: React.FC<InvoiceSettingsModalProps> = ({ isOpen, onClose }) => {
    const { token } = useAuth();
    const [settings, setSettings] = useState<InvoiceSettings>({
        price_tolerance_percent: 1.0,
        quantity_strict_match: true,
        auto_approve_limit: 0
    });
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // Fetch settings on open
    useEffect(() => {
        if (isOpen && token) {
            fetchSettings();
        }
    }, [isOpen, token]);

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const res = await fetch('/api/invoices/settings', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success && data.data) {
                setSettings({
                    price_tolerance_percent: data.data.price_tolerance_percent ?? 1.0,
                    quantity_strict_match: data.data.quantity_strict_match ?? true,
                    auto_approve_limit: data.data.auto_approve_limit ?? 0
                });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);
        try {
            const res = await fetch('/api/invoices/settings', {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(settings)
            });
            const result = await res.json();
            if (result.success) {
                setMessage({ type: 'success', text: 'Settings updated successfully' });
                setTimeout(onClose, 1500);
            } else {
                setMessage({ type: 'error', text: result.message || 'Failed to update' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Network error' });
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="invoice-settings-modal-overlay">
            <div className="invoice-settings-modal">
                <div className="modal-header">
                    <h2>Matching Configuration</h2>
                    <button onClick={onClose} className="close-btn">&times;</button>
                </div>

                <div className="modal-body">
                    {isLoading ? <p>Loading settings...</p> : (
                        <div className="settings-form">
                            <div className="form-group">
                                <label>Price Tolerance (%)</label>
                                <p className="help-text">Allowable difference percentage between Invoice and Quote price.</p>
                                <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    value={settings.price_tolerance_percent}
                                    onChange={e => setSettings({ ...settings, price_tolerance_percent: parseFloat(e.target.value) })}
                                />
                            </div>

                            <div className="form-group checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={settings.quantity_strict_match}
                                        onChange={e => setSettings({ ...settings, quantity_strict_match: e.target.checked })}
                                    />
                                    Strict Quantity Match
                                </label>
                                <p className="help-text">If enabled, any quantity difference will be flagged as mismatch.</p>
                            </div>

                            <div className="form-group">
                                <label>Auto-Approve Limit (Currency)</label>
                                <p className="help-text">Automatically approve discrepancies below this amount.</p>
                                <input
                                    type="number"
                                    step="1"
                                    min="0"
                                    value={settings.auto_approve_limit}
                                    onChange={e => setSettings({ ...settings, auto_approve_limit: parseFloat(e.target.value) })}
                                />
                            </div>
                        </div>
                    )}

                    {message && (
                        <div className={`message ${message.type}`}>{message.text}</div>
                    )}
                </div>

                <div className="modal-footer">
                    <button onClick={onClose} disabled={saving}>Cancel</button>
                    <button onClick={handleSave} className="save-btn" disabled={saving}>
                        {saving ? 'Saving...' : 'Save Configuration'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InvoiceSettingsModal;
